import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, api } from './api';
import { t } from './i18n';

export type AppCheckMode = 'off' | 'monitor' | 'enforce';
export interface AppTicket {
  id: string;
  sms_to: string;
  body: string;
  needs_line: boolean;
}
export interface AppSettings {
  min_version: { android: string; ios: string };
  latest_version: { android: string; ios: string };
  store_url: { android: string; ios: string };
  tickets: AppTicket[];
  contact: { phone: string; viber: string; email: string; hours: string };
  features: { station_ads: boolean; reports: boolean };
  app_check_mode: AppCheckMode;
}
export interface AppUsage {
  hours: {
    hour: string;
    valid: number;
    missing: number;
    invalid: number;
    reused: number;
    v1: number;
    v2: number;
  }[];
  installs: { platform: 'android' | 'ios'; total: number; active_7d: number; push: number }[];
  versions: { platform: string; version: string; n: number }[];
}

export function useAppSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['app-settings'],
    queryFn: () => api<{ settings: AppSettings }>('/settings/app'),
    enabled,
  });
}

export function useSaveAppSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (s: AppSettings) =>
      api<{ settings: AppSettings }>('/settings/app', { method: 'PUT', body: s }),
    onSuccess: (data) => {
      qc.setQueryData(['app-settings'], data);
      qc.invalidateQueries({ queryKey: ['app-usage'] });
      toast.success(t('Saved'));
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('Could not save')),
  });
}

export function useAppUsage() {
  return useQuery({
    queryKey: ['app-usage'],
    queryFn: () => api<AppUsage>('/app/usage'),
    refetchInterval: 60_000,
  });
}
